
import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Trash2, Calendar as CalendarIcon, Clock, Plus, RefreshCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface EmailTemplate {
  id: string;
  template_id: string;
  name: string;
}

interface ScheduledEmail {
  id: string;
  template_id: string;
  subject: string;
  recipients: string[];
  variables: Record<string, any> | null;
  scheduled_at: string;
  recurring_pattern: string | null;
  status: string;
  created_at: string;
  sent_at: string | null;
}

export function ScheduleManager() {
  const [emails, setEmails] = useState<ScheduledEmail[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [recipients, setRecipients] = useState<string>('');
  const [variables, setVariables] = useState<string>('{}');
  const [date, setDate] = useState<Date>(new Date());
  const [time, setTime] = useState<string>('12:00');
  const [recurringPattern, setRecurringPattern] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('upcoming');
  const { toast } = useToast();

  useEffect(() => {
    fetchScheduledEmails();
    fetchEmailTemplates();
  }, [activeTab]);

  const fetchScheduledEmails = async () => {
    setLoading(true);
    try {
      // Filter based on active tab
      let query = supabase
        .from('email_schedule')
        .select('*');

      if (activeTab === 'upcoming') {
        query = query.in('status', ['pending', 'processing']);
      } else if (activeTab === 'sent') {
        query = query.eq('status', 'sent');
      } else if (activeTab === 'failed') {
        query = query.eq('status', 'failed');
      } else if (activeTab === 'cancelled') {
        query = query.eq('status', 'cancelled');
      }

      const { data, error } = await query.order('scheduled_at', { ascending: true });
      
      if (error) throw error;
      
      // Transform the data to match the ScheduledEmail interface
      const transformedData: ScheduledEmail[] = data?.map(item => ({
        ...item,
        recipients: Array.isArray(item.recipients) 
          ? item.recipients.map(r => String(r)) // Convert each recipient to string
          : typeof item.recipients === 'string' 
            ? [item.recipients] 
            : [], // Handle case if it's a single string or empty
        variables: item.variables as Record<string, any> | null
      })) || [];
      
      setEmails(transformedData);
    } catch (error: any) {
      console.error('Error fetching scheduled emails:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to load scheduled emails',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, template_id, name')
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setTemplates(data || []);
    } catch (error: any) {
      console.error('Error fetching email templates:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to load email templates',
      });
    }
  };

  const handleNewSchedule = () => {
    setSelectedTemplate('');
    setEmailSubject('');
    setRecipients('');
    setVariables('{}');
    setDate(new Date());
    setTime('12:00');
    setRecurringPattern('');
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (!selectedTemplate) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Please select an email template',
        });
        return;
      }

      if (!recipients.trim()) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Please enter at least one recipient',
        });
        return;
      }

      // Parse variables
      let parsedVariables = {};
      try {
        parsedVariables = JSON.parse(variables);
      } catch (err) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Variables must be valid JSON',
        });
        return;
      }

      // Parse recipients
      const recipientList = recipients
        .split(',')
        .map(email => email.trim())
        .filter(email => email);

      // Combine date and time
      const [hours, minutes] = time.split(':').map(Number);
      const scheduledDate = new Date(date);
      scheduledDate.setHours(hours, minutes, 0, 0);

      // If scheduled date is in the past, show an error
      if (scheduledDate < new Date()) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Scheduled time must be in the future',
        });
        return;
      }

      const { error } = await supabase
        .from('email_schedule')
        .insert({
          template_id: selectedTemplate,
          subject: emailSubject,
          recipients: recipientList,
          variables: parsedVariables,
          scheduled_at: scheduledDate.toISOString(),
          recurring_pattern: recurringPattern || null,
          status: 'pending',
        });

      if (error) throw error;
      
      toast({
        title: 'Email Scheduled',
        description: `Email scheduled for ${format(scheduledDate, 'PPP p')}`,
      });
      
      setShowDialog(false);
      fetchScheduledEmails();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to schedule email',
      });
    }
  };

  const handleCancelEmail = async (id: string) => {
    try {
      const { error } = await supabase
        .from('email_schedule')
        .update({ status: 'cancelled' })
        .eq('id', id);

      if (error) throw error;
      
      // Update the emails list without refetching
      setEmails(emails.map(email => 
        email.id === id ? { ...email, status: 'cancelled' } : email
      ));
      
      toast({
        title: 'Email Cancelled',
        description: 'The scheduled email has been cancelled',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to cancel email',
      });
    }
  };

  const getTemplateName = (templateId: string) => {
    const template = templates.find(t => t.template_id === templateId);
    return template ? template.name : templateId;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Pending</Badge>;
      case 'processing':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Processing</Badge>;
      case 'sent':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Sent</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Cancelled</Badge>;
      case 'failed':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPP p');
    } catch (err) {
      return dateString;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Email Scheduling</h3>
        <Button onClick={handleNewSchedule} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Schedule Email
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-4">
          <div className="border rounded-md">
            {loading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-64 flex-1" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                ))}
              </div>
            ) : emails.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No {activeTab} emails found.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Scheduled For</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emails.map((email) => (
                    <TableRow key={email.id}>
                      <TableCell className="font-medium">
                        {getTemplateName(email.template_id)}
                        {email.subject && <div className="text-xs text-muted-foreground">Subject: {email.subject}</div>}
                      </TableCell>
                      <TableCell>
                        {Array.isArray(email.recipients) 
                          ? email.recipients.length > 3 
                            ? `${email.recipients.slice(0, 3).join(', ')} +${email.recipients.length - 3} more` 
                            : email.recipients.join(', ')
                          : 'No recipients'
                        }
                      </TableCell>
                      <TableCell>
                        {formatDateTime(email.scheduled_at)}
                        {email.recurring_pattern && (
                          <div className="flex items-center text-xs text-muted-foreground mt-1">
                            <RefreshCcw className="h-3 w-3 mr-1" />
                            {email.recurring_pattern}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(email.status)}
                        {email.sent_at && <div className="text-xs text-muted-foreground mt-1">Sent: {formatDateTime(email.sent_at)}</div>}
                      </TableCell>
                      <TableCell>
                        {(email.status === 'pending' || email.status === 'processing') && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleCancelEmail(email.id)}
                            title="Cancel"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Email</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Email Template</label>
              <select 
                className="p-2 border rounded-md"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
              >
                <option value="">Select a template...</option>
                {templates.map(template => (
                  <option key={template.id} value={template.template_id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Subject (Optional)</label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Custom subject (overrides template subject)"
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Recipients</label>
              <Textarea
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                placeholder="Email addresses separated by commas"
                className="min-h-[80px]"
              />
              <p className="text-xs text-muted-foreground">
                Enter email addresses separated by commas
              </p>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Variables (JSON)</label>
              <Textarea
                value={variables}
                onChange={(e) => setVariables(e.target.value)}
                placeholder='{"name": "John", "company": "ACME"}'
                className="min-h-[80px] font-mono text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium">Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(date) => date && setDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="grid gap-2">
                <label className="text-sm font-medium">Time</label>
                <div className="flex items-center border rounded-md">
                  <Clock className="h-4 w-4 mx-3 text-muted-foreground" />
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="flex-1 p-2 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid gap-2">
              <label className="text-sm font-medium">Recurring Pattern (Optional)</label>
              <select
                className="p-2 border rounded-md"
                value={recurringPattern}
                onChange={(e) => setRecurringPattern(e.target.value)}
              >
                <option value="">No Recurrence</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
